/**
 * Tooltip — 호버/포커스 시 앵커 위에 팝오버를 띄우는 공용 컴포넌트.
 *
 * 디자인 선택:
 *  - 접근성: button/a 래퍼 대신 `role="tooltip"` + `aria-describedby` 로 스크린리더 대응
 *  - 트리거: hover + focus-visible (키보드 탭 이동) 양쪽 지원
 *  - 위치: 기본 앵커 '아래 중앙' — viewport overflow 시 위로 뒤집힘
 *  - 포털: docs-site 에서 드로어/모달 stacking context 밖으로 꺼내기 위해 body Portal
 *
 * docs-site · dashboard · playground 세 곳 공용. CSS 는 CSS Modules 로
 * 각 호스트의 디자인 토큰(--pk-*, --ifm-*) 을 참조한다.
 */
import { type ReactNode, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import styles from "./Tooltip.module.css"

export interface TooltipProps {
	/** 호버 대상 — 보통 `<Term>` 의 텍스트 */
	readonly children: ReactNode
	/** 팝오버 안에 들어갈 내용 — 문자열 또는 ReactNode */
	readonly content: ReactNode
	/** 팝오버 최대 폭 (px). 기본 320 */
	readonly maxWidth?: number
	/** disabled: 툴팁을 절대 띄우지 않음 (content 가 빈값일 때 자동으로 true) */
	readonly disabled?: boolean
}

export function Tooltip({ children, content, maxWidth = 320, disabled }: TooltipProps) {
	const id = useId()
	const anchorRef = useRef<HTMLSpanElement | null>(null)
	const [open, setOpen] = useState(false)
	const [pos, setPos] = useState<{ top: number; left: number; placement: "top" | "bottom" } | null>(null)

	const effectiveDisabled = disabled || !content
	const show = useCallback(() => {
		if (!effectiveDisabled) setOpen(true)
	}, [effectiveDisabled])
	const hide = useCallback(() => setOpen(false), [])

	// 위치 계산 — 앵커 기준으로 viewport 안에 들어가도록 top/bottom 자동 선택
	useLayoutEffect(() => {
		if (!open || !anchorRef.current) return
		const rect = anchorRef.current.getBoundingClientRect()
		const gap = 8
		const below = rect.bottom + gap
		const placeAbove = below + 120 > window.innerHeight
		const top = placeAbove ? rect.top - gap : below
		const leftRaw = rect.left + rect.width / 2
		// horizontal clamp — 팝오버는 transform translateX(-50%) 로 중앙 정렬되므로 left 는 중앙값
		const left = Math.max(12 + maxWidth / 2, Math.min(window.innerWidth - 12 - maxWidth / 2, leftRaw))
		setPos({ top, left, placement: placeAbove ? "top" : "bottom" })
	}, [open, maxWidth])

	// Esc 로 닫기
	useEffect(() => {
		if (!open) return
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") setOpen(false)
		}
		window.addEventListener("keydown", onKey)
		return () => window.removeEventListener("keydown", onKey)
	}, [open])

	return (
		<>
			<span
				ref={anchorRef}
				className={styles.anchor}
				// biome-ignore lint/a11y/noNoninteractiveTabindex: 툴팁 앵커는 키보드 포커스로 툴팁을 열 수 있어야 하므로 tabIndex=0 필요
				tabIndex={0}
				aria-describedby={open ? id : undefined}
				onMouseEnter={show}
				onMouseLeave={hide}
				onFocus={show}
				onBlur={hide}
			>
				{children}
			</span>
			{open && pos && typeof document !== "undefined"
				? createPortal(
						<div
							id={id}
							role="tooltip"
							className={`${styles.popover} ${pos.placement === "top" ? styles.placementTop : styles.placementBottom}`}
							style={{ top: pos.top, left: pos.left, maxWidth }}
						>
							{content}
						</div>,
						document.body
					)
				: null}
		</>
	)
}
